import { Box, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import WaitingPlayerCard from './WaitingPlayerCard';

const getPlayerPoints = (p) => p.totalPoints ?? (p.wins - p.losses);

// Define a seção (chave + título) a que um jogador pertence, conforme o tempo de espera.
const sectionInfo = (player) => {
  const { waitingFor, checkedInNow } = player;
  if (waitingFor >= 10) return { key: 'priority', title: 'Próximos jogadores' };
  if (waitingFor < 0) return { key: 'checkin', title: 'Check-in na próxima' };
  if (waitingFor === 0) {
    return checkedInNow
      ? { key: 'checkinNow', title: 'Check-in agora' }
      : { key: 'w0', title: 'Saiu agora' };
  }
  const whole = Math.floor(waitingFor);
  const hasHalf = !Number.isInteger(waitingFor);
  if (whole === 0) return { key: 'half', title: 'Meia partida aguardando' };
  return {
    key: `w${whole}${hasHalf ? 'h' : ''}`,
    title: `${whole}ª partida${hasHalf ? ' e meia' : ''} aguardando`,
  };
};

export function PlayersQueue() {
  const players = useSelector((state) => state.players);

  const totalPlayers = players.filter((p) => p.checkedIn).length;

  const playersQueue = players
    .filter((p) => !p.isPlaying && p.checkedIn)
    // waitingFor desc; entre os de mesmo valor, "Check-in agora" agrupado (fica contíguo).
    .sort((a, b) => (b.waitingFor - a.waitingFor)
      || ((b.checkedInNow ? 1 : 0) - (a.checkedInNow ? 1 : 0)));

  // Agrupa em seções na ordem da fila (prioridade no topo, depois esperas desc, check-in no fim).
  const sections = playersQueue.reduce((acc, player) => {
    const { key, title } = sectionInfo(player);
    const last = acc[acc.length - 1];
    if (last && last.key === key) {
      last.players.push(player);
    } else {
      acc.push({ key, title, players: [player] });
    }
    return acc;
  }, []);

  return (
    <Box
      sx={{
        mt: 4,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Typography variant="h5" textAlign="center">
        {`${totalPlayers} jogadores em quadra:`}
      </Typography>
      <Box
        sx={{
          mt: 2,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 2,
          px: { xs: 2, md: 2 },
        }}
      >
        {sections.length > 0
          ? sections.map((section) => (
            <Box
              key={section.key}
              sx={{
                width: '100%',
                bgcolor: 'rgba(0, 0, 0, 0.035)',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                borderRadius: 3,
                p: 2,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, mb: 1, textAlign: 'center' }}
              >
                {section.key === 'priority'
                  ? `${section.title} (${section.players.reduce((sum, p) => sum + getPlayerPoints(p), 0)})`
                  : section.title}
              </Typography>
              <Box sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', pl: { xs: 1, md: 2 },
              }}
              >
                {section.players.map((player) => (
                  <WaitingPlayerCard key={`${player.playerId}-${player.playerName}`} player={player} fullWidth />
                ))}
              </Box>
            </Box>
          ))
          : <Typography textAlign="center">Nenhum jogador em quadra</Typography>}
      </Box>
    </Box>
  );
}
